import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'
import { createSelector } from '@reduxjs/toolkit'

import { clearMusicSource } from '../../reducers/sound'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'

import KeyIssueTile from './components/key-issue-tile'
import FilterButtons from '../elements/filter-buttons'
import KeyIssueSummary from './components/summary'
import Paginator from '../utils/paginator'
import MarkdownWrapper from '../utils/markdown-wrapper'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/underline-01.svg'
import anime from 'animejs'
import { useNavigate } from 'react-router-dom'

const selectPager = state => state.pager.keyIssues
const selectKeyIssues = state => state.keyIssues

const makeKeyIssues = createSelector(
    [selectPager, selectKeyIssues],
    (pager, keyIssues) => {
        if (pager) {
            return keyIssues
                .filter(item => pager.itemIds.indexOf(item.uuid) !== -1)
                .sort((a, b) => {
                    if (pager.itemIds.indexOf(a.id) > pager.itemIds.indexOf(b.id)) {
                        return 1
                    }
                    return -1
                })
        }
        return []
    }
)

const mapStateToProps = (state) => {
    const pager = selectPager(state)
    const keyIssues = makeKeyIssues(state)

    return {
        pager,
        keyIssues,
        keyIssueCategories: state.keyIssueCategories,
        navState: state.nav
    }
}

const KeyIssuesIndex = ({ dispatch, navState, pager, keyIssues, keyIssueCategories }) => {
    const componentName = 'Key Issues'
    const urlCategory = new URLSearchParams(window.location.search).get('category')
    const [selectedFilter, setSelectedFilter] = useState(urlCategory || null)
    const navigate = useNavigate()

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            if (navState.theme !== 'grey') {
                dispatch(saveNavTheme('grey'))
            }
            handlePreloadingState(urlCategory)
        } else if (componentState === 'ANIMATING_IN') {
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (urlCategory !== selectedFilter) {
            let params = {}
            if (urlCategory) {
                params.category = urlCategory
            }
            dispatch(fetchApiData(apiResourceEndpoint('key-issues', 'LIST'), params))
            setSelectedFilter(urlCategory)
        }
    }, [urlCategory, selectedFilter])

    const handlePreloadingState = async () => {
        let params = {}
        if (urlCategory) {
            params.category = urlCategory
        }
        dispatch(fetchApiData(apiResourceEndpoint('key-issues', 'LIST'), params))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        dispatch(clearMusicSource())
        const markerLine = animateLine()
        markerLine.finished.then(() => {
            dispatch(completeAnimatingIn())
        })
    }

    const handleAnimatingOutState = () => {
        setSelectedFilter(null)
        dispatch(completeAnimatingOut())
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    const handleFilterClick = (clickedFilter) => {
        // clicking the already-selected category clears the filter
        const filter = clickedFilter === selectedFilter ? null : clickedFilter
        if (filter !== selectedFilter) {
            let params = {}
            if (filter) {
                params.category = filter
                navigate(buildRoutePath(routes.keyIssuesIndex.path, {}, { category: filter }))
            } else {
                navigate(buildRoutePath(routes.keyIssuesIndex.path, {}, {}))
            }
            setTimeout(() => {
                let app = document.getElementById('app')
                if (app) {
                    app.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }
            }, 100)
        }
    }

    const handlePageClick = (page) => {
        let params = { page }
        if (selectedFilter) {
            params.category = selectedFilter
        }
        dispatch(fetchApiData(apiResourceEndpoint('key-issues', 'LIST'), params))
        setTimeout(() => {
            // make sure the scroll to top on window and containers
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
            let app = document.getElementById('app')
            if (app) {
                app.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }, 100)
    }

    if (componentState === 'PRELOADING') {
        return null
    }

    const animateLine = () => {
        return anime({
            targets: '.title-stroke #marker',
            strokeDashoffset: 0,
            easing: 'easeInQuart',
            duration: 750
        })
    }

    const pageBlurb = 'Systemic racism draws its power from the way key policies and practices in vital areas of society interconnect. Explore the issues to understand their impact.'

    return (
        <div className="key-issues-index">
            <div className="container">
                <div className="title-stroke">
                    <h1>Key Issues</h1>
                    <MarkerStroke />
                </div>
                <div className="page-blurb">
                    <MarkdownWrapper content={pageBlurb} />
                </div>

                <FilterButtons onHandleFilterClick={handleFilterClick} selectedFilter={selectedFilter} filterObjects={keyIssueCategories} colorWhenUnfiltered />

                {selectedFilter &&
                    <KeyIssueSummary selectedFilter={selectedFilter} filterObjects={keyIssueCategories} />
                }

                <div className="key-issues-list">
                    {keyIssues.map((keyIssue) => (
                        <KeyIssueTile key={keyIssue.uuid} keyIssue={keyIssue} filterObjects={keyIssueCategories} />
                    ))}
                </div>
                <Paginator pager={pager} onSetCurrentPage={handlePageClick} />
            </div>
        </div>
    )
}

export default connect(mapStateToProps)(KeyIssuesIndex)
